import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, PINNED_CENTER_ID } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';

export default function LoginPage({ superAdmin = false }) {
  const { profile, login, logout, loading, authError } = useAuth();
  const { settings } = useSettings();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [instituteCode, setInstituteCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [centerBrand, setCenterBrand] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const websiteTenantId = !superAdmin ? String(searchParams.get('tenant')||'').trim() : '';
  const inferredTenantId = PINNED_CENTER_ID || websiteTenantId;

  useEffect(() => {
    if (superAdmin || !inferredTenantId) return;
    getDoc(doc(db, 'publicInstitutes', inferredTenantId)).then((snap) => {
      if (snap.exists()) setCenterBrand(snap.data());
    }).catch(() => {});
  }, [superAdmin, inferredTenantId]);

  if (!loading && profile) {
    if (superAdmin && profile.isSuperAdmin) return <Navigate to="/superadmin/dashboard" replace />;
    if (!superAdmin && !profile.isSuperAdmin) return <Navigate to="/institute/dashboard" replace />;
    if (superAdmin && !profile.isSuperAdmin) return <div className="access-denied"><span>🔐</span><h1>Wrong portal</h1><p>This login is reserved for Super Admin accounts.</p><button className="button button-primary" onClick={logout}>Sign out</button></div>;
    if (!superAdmin && profile.isSuperAdmin) return <div className="access-denied"><span>🔐</span><h1>Wrong portal</h1><p>Use the Super Admin login for this account.</p><button className="button button-primary" onClick={logout}>Sign out</button></div>;
  }

  const brand = superAdmin ? settings : (centerBrand || settings);
  const brandName = superAdmin ? (brand.platformName || 'CoachSphere') : (brand.name || brand.displayName || 'Coaching Management');

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const data = await login(email, password, superAdmin ? '' : (inferredTenantId ? '' : instituteCode), superAdmin ? '' : inferredTenantId);
      if (superAdmin && !data.isSuperAdmin) { await logout(); throw new Error('This login is reserved for the Super Admin portal.'); }
      if (!superAdmin && data.isSuperAdmin) { await logout(); throw new Error('Use the Super Admin login for this account.'); }
      const destination = location.state?.from || (superAdmin ? '/superadmin/dashboard' : '/institute/dashboard');
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err?.message?.replace('Firebase: ', '') || 'Unable to sign in.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-art">
        <div className="login-orb one" /><div className="login-orb two" />
        <div className="login-art-content">
          <span className="login-badge">✦ {superAdmin ? 'SAAS CONTROL PLANE' : 'INSTITUTE WORKSPACE'}</span>
          <h1>{superAdmin ? 'Run every coaching center from one command center.' : 'Teach, manage and grow from one beautiful workspace.'}</h1>
          <p>{superAdmin ? 'Institutes, plans, subscriptions, security and platform settings in the same unified application.' : 'Admissions, students, faculty, academics, attendance, tests, fees, payroll and reports stay connected.'}</p>
          <div className="login-feature-grid">
            {(superAdmin ? ['🏫 Multi-center control', '💎 Plans & subscriptions', '🛡️ Platform security', '📈 SaaS analytics'] : ['🎯 Admissions CRM', '🎓 Academics', '✅ Attendance', '💰 Finance']).map((item) => <div key={item}>{item}</div>)}
          </div>
        </div>
      </div>
      <div className="login-form-side">
        <form className="login-card" onSubmit={submit}>
          <div className="login-logo-row">
            {brand.logoUrl ? <img src={brand.logoUrl} alt="Logo" /> : <div className="login-logo-placeholder">🎓</div>}
            <div><strong>{brandName}</strong><span>{superAdmin ? 'Super Admin Portal' : 'Secure Institute Login'}</span></div>
          </div>
          <div className="login-copy"><span>WELCOME BACK</span><h2>Sign in to continue</h2><p>Use the account created for your assigned role.</p></div>
          {(error || authError) && <div className="form-error">⚠ {error || authError}</div>}
          {!superAdmin&&!inferredTenantId&&<label className="login-field"><span>Institute / School Code</span><input id="login-institute-code" name="instituteCode" value={instituteCode} onChange={(e)=>setInstituteCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g,''))} placeholder="e.g. DPS001" required autoComplete="organization"/><small>Use the unique code issued to your school/college/coaching institute. The same code can be used by the mobile app.</small></label>}<label className="login-field"><span>Email address</span><input id="login-email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" autoComplete="username" required /></label>
          <label className="login-field"><span>Password</span><input id="login-password" name="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" autoComplete="current-password" required /></label>
          <button className="login-button" disabled={submitting}>{submitting ? 'Signing in...' : 'Sign in securely →'}</button>
          {!superAdmin&&inferredTenantId&&<div className="login-tenant-hint">🏫 Institute detected from this school's website. No school code is required.</div>}<div className="login-footnote">🔒 Secure Authentication • role-based access • tenant isolation</div>
        </form>
      </div>
    </div>
  );
}
